import { useEffect, useRef, useState } from "react";
import { useAuth } from "../../lib/auth.js";
import { useT } from "../../lib/i18n.js";
import { trpcClient, errorMessage } from "../../lib/trpc.js";
import { hasGroupKey, openGroupKey, ownPublicKey, proveDeviceKey } from "../../lib/keyring.js";
import { restorePhotosFromFile, downloadRecoveryFile, helpApprovalSchema, readSmallJson, type PendingRecovery } from "../../lib/recovery.js";
import { openFilePicker } from "../../lib/filePicker.js";
import { Button } from "../../components/ui/Button.js";

type GroupProgress = Awaited<ReturnType<typeof trpcClient.recovery.progress.query>>[number] & { ready: boolean };
type PhotoRequest = PendingRecovery & { expiresAt: number };

export function PhotoRecovery() {
  const t = useT();
  const { user } = useAuth();
  const [request, setRequest] = useState<PhotoRequest | null>(null);
  const [groups, setGroups] = useState<GroupProgress[] | null>(null);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [fileRestored, setFileRestored] = useState<number | null>(null);
  const [restoredGroup, setRestoredGroup] = useState<string | null>(null);
  const mounted = useRef(true);

  async function refresh(activeRequest: PhotoRequest | null) {
    const active = activeRequest && activeRequest.expiresAt > Date.now() ? activeRequest : null;
    const progress = await trpcClient.recovery.progress.query({ ticket: active?.ticket });
    const checked = await Promise.all(progress.map(async (group) => {
      let ready = !group.encrypted || hasGroupKey(group.groupId);
      if (!ready && group.wrappedGroupKey) {
        try { await openGroupKey(group.groupId, group.wrappedGroupKey); ready = true; }
        catch { /* An old grant can exist without being decryptable by the new account key. */ }
      }
      return { ...group, ready };
    }));
    if (mounted.current) { setGroups(checked); if (!active) setRequest(null); }
  }
  useEffect(() => {
    mounted.current = true;
    // One fetch on this active account screen. Updates thereafter require a user action.
    void refresh(null).catch(caught => { if (mounted.current) setError(errorMessage(caught)); });
    return () => { mounted.current = false; };
  }, []);

  async function work(action: () => Promise<void>) {
    setBusy(true); setError(null);
    try { await action(); } catch (caught) { setError(errorMessage(caught)); }
    finally { setBusy(false); }
  }
  function download(active: PhotoRequest) {
    downloadRecoveryFile("myfriends-photo-request.json", { format: "myfriends-help-v1", username: user!.username, ticket: active.ticket, fingerprint: active.fingerprint });
  }
  const readyCount = groups?.filter(g => g.ready).length ?? 0;
  return <div className="fc-recovery__form">
    <h3>{t("Restore group photos")}</h3>
    <p>{t("You need one trusted member who can open the photos from each group that needs help. You do not need approval from every member. One person can help with several shared groups.")}</p>
    <p>{t("Download one request and send the same file to those members. They approve it in each group's settings and send back a reply for that group. Import each reply here.")}</p>
    <Button variant="secondary" disabled={busy} onClick={() => openFilePicker({ accept: ".json", onPick: files => {
      if (files[0] && user) void work(async () => {
        const count = await restorePhotosFromFile(files[0]!, user.id, user.username);
        setFileRestored(count); await refresh(request);
      });
    } })}>{t("Restore photos with my recovery file")}</Button>
    {fileRestored !== null && <p role="status">{fileRestored > 0
      ? t("{count} groups restored from your recovery file.").replace("{count}", String(fileRestored))
      : t("This file did not restore additional groups. Already available groups need no action; ask a member for any groups still locked.")}</p>}
    <div className="fc-recovery__actions">
      <Button variant="secondary" loading={busy} onClick={() => void work(async () => {
        const publicKey = ownPublicKey();
        if (!user || !publicKey) return;
        const started = await trpcClient.recovery.begin.mutate({ username: user.username, publicKey, method: "photos" });
        const active = { ...started, proof: await proveDeviceKey(started.challenge), expiresAt: Date.now() + 15 * 60_000 };
        setRequest(active); setRestoredGroup(null); download(active); await refresh(active);
      })}>{request ? t("Create a new photo request") : t("Download one request for my groups")}</Button>
      <Button variant="secondary" disabled={busy} onClick={() => void work(() => refresh(request))}>{t("Refresh approvals")}</Button>
    </div>
    <p className="fc-account__hint">{t("Approvals update when you press Refresh approvals. We do not check in the background.")}</p>
    {request && <>
      <p>{t("Keep this page open. The photo request lasts 15 minutes and can restore several groups. Download it again to send the same request to another member.")}</p>
      <p>{t("Confirm your identity in person or on a call and compare this fingerprint with each helper:")}</p>
      <code className="fc-recovery__fingerprint">{request.fingerprint}</code>
      <div className="fc-recovery__actions">
        <Button variant="secondary" disabled={busy} onClick={() => download(request)}>{t("Download request again")}</Button>
        <Button variant="secondary" disabled={busy} onClick={() => openFilePicker({ accept: ".json", onPick: (files) => { if (files[0]) void work(async () => {
          const { approval } = helpApprovalSchema.parse(await readSmallJson(files[0]!));
          const result = await trpcClient.recovery.finishPhotos.mutate({ ticket: approval, proof: request.proof! });
          setRestoredGroup(result.groupId);
          // Other groups use the same nonce/key proof. Do not discard it after the first reply.
          await refresh(request);
        }); } })}>{t("Import a group's reply")}</Button>
      </div>
    </>}
    {groups && <>
      <p role="status">{t("{ready} of {total} groups available").replace("{ready}", String(readyCount)).replace("{total}", String(groups.length))}</p>
      {groups.length === 0 && <p>{t("You have no active groups to restore.")}</p>}
      <ul className="fc-recovery__groups" aria-label={t("Group recovery progress")}>
        {groups.map(group => <li key={group.groupId} className={`fc-recovery__group fc-recovery__group--${group.ready ? "ready" : "waiting"}`}>
          <strong>{group.name}</strong>
          <p>{group.ready ? t("Photos available") : group.approvals.length ? t("Approved — import a reply to restore photos") : t("Needs help from one group member")}</p>
          {group.approvals.map(member => <p className="fc-recovery__member" key={member.helperId}>{t("Approved by")}: {member.name} (@{member.username})</p>)}
          {group.ready && group.providedBy && <p className="fc-recovery__member">{t("Access provided by")}: {group.providedBy} (@{group.providedByUsername})</p>}
          {group.ready && <a href={`/g/${group.groupId}`}>{t("Open group photos")}</a>}
        </li>)}
      </ul>
    </>}
    {restoredGroup && <p role="status">{t("Group access restored. You can import the next group's reply using the same request.")}</p>}
    {error && <p role="alert" className="fc-auth__error">{error}</p>}
  </div>;
}
